import { Typography } from '@euler/app/components/typography';
import { VehicleBrandLogo } from '@euler/app/components/VehicleBrandLogo';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useShowQrcodeSharingMoreActions } from '@euler/app/flows/common/functions/useShowQrcodeSharingMoreActions';
import { TaskBookmarkStatusView } from '@euler/app/tv/components/TaskBookmarkStatusView';
import { VerticalLine } from '@euler/app/tv/components/VerticalLine';
import { kVehicleBannerHeight } from '@euler/app/tv/functions/constants';
import type { TaskNavParams } from '@euler/app/tv/pages/TaskFlow';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { PresentationTheme } from '@euler/app/tv/theme/types';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { MaybeText } from '@euler/components/MaybeText';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { TaskBookmarkSavePayload } from '@euler/model';
import { InspectionTaskStatus } from '@euler/model/enum';
import { TaskDetail, TaskExtraInfo } from '@euler/model/task-detail';
import { formatDateTime } from '@euler/utils/formatters';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { LinearGradient } from 'expo-linear-gradient';
import {
  memo,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  ScrollView,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

const Cell = memo(
  ({
    theme,
    text,
    label,
    style,
  }: {
    theme: PresentationTheme;
    text: ReactNode | string;
    label: string;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={style}>
        <View
          css={`
            margin-bottom: ${rpx(11)}px;
          `}
        >
          <MaybeText
            text={text}
            css={`
              color: ${theme.task.detail.vehicleBanner.infoValueColor};
              font-size: ${rpx(30)}px;
            `}
          />
        </View>
        <Typography.Label
          light
          color={theme.task.detail.vehicleBanner.infoLabelColor}
          size={rpx(18)}
        >
          {label}
        </Typography.Label>
      </View>
    );
  },
);

const ObserveStatus = memo(
  ({
    hasObserved,
    color,
    onPress,
    style,
  }: {
    hasObserved?: boolean;
    color: string;
    style?: StyleProp<ViewStyle>;
    onPress?: (ref: RefObject<View>) => void;
  }) => {
    const ref = useRef<TouchableOpacity>(null);
    const { rpx } = useResponsiveContext();
    const handlePress = useCallback(() => {
      onPress?.(ref as any);
    }, [onPress]);
    return (
      <TouchableOpacity
        css={`
          flex-direction: row;
          align-items: center;
          justify-content: center;
          border-color: ${color};
          border-width: 1px;
          padding-left: ${rpx(10)}px;
          padding-right: ${rpx(10)}px;
          border-radius: ${rpx(8)}px;
        `}
        style={style}
        onPress={handlePress}
      >
        <Typography.Label regular color={color} size={rpx(24)}>
          {hasObserved ? '已关注' : '未关注'}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);

const Separator = memo(() => {
  const theme = usePresentationTheme();
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        height: 100%;
      `}
    >
      <VerticalLine
        startColor={theme.task.detail.vehicleBanner.separatorStartColor}
        endColor={theme.task.detail.vehicleBanner.separatorEndColor}
        css={`
          margin-top: ${rpx(5)}px;
          margin-bottom: ${rpx(5)}px;
          margin-left: ${rpx(50)}px;
          margin-right: ${rpx(50)}px;
          flex: 1;
        `}
      />
    </View>
  );
});

export const VehicleInfoBannerView = memo(
  ({
    detail,
    extraInfo,
    style,
  }: {
    style?: StyleProp<ViewStyle>;
    detail: TaskDetail;
    extraInfo: TaskExtraInfo | undefined;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();

    const { basicInfo } = detail;
    const { store } = useCurrentUserDangerously();
    const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();

    const hasObserved =
      extraInfo?.observes != null && extraInfo.observes.length > 0;

    const showMoreActions = useShowQrcodeSharingMoreActions({
      onViewSubscriptions: () => {
        navigation.navigate('_vehicleSubscriptions', {
          licensePlateNo: detail.basicInfo.licensePlateNo,
        });
      },
    });

    const onQrcodeSharingPress = useCallback(
      (buttonRef: RefObject<View>) => {
        navigation.navigate('_qrcodeSharing', {
          taskNo: detail.basicInfo.taskNo,
          orderNo: detail.basicInfo.orderNo,
          reportNo: detail.basicInfo.reportNo,
          licensePlateNo: detail.basicInfo.licensePlateNo,
          storeName: store.name,
          onClose: origin => {
            if (origin === 'more') {
              showMoreActions({ anchor: buttonRef });
            }
          },
        });
      },
      [
        navigation,
        detail.basicInfo.taskNo,
        detail.basicInfo.orderNo,
        detail.basicInfo.reportNo,
        detail.basicInfo.licensePlateNo,
        store.name,
        showMoreActions,
      ],
    );

    const bookmarkPayload = useMemo<TaskBookmarkSavePayload>(() => {
      return {
        taskNo: basicInfo.taskNo,
        licensePlateNo: basicInfo.licensePlateNo,
        vin: basicInfo.vin,
        vehicleName: basicInfo.vehicleName,
        vehicleBrandName: basicInfo.vehicleBrandName,
        vehicleBrandLogo: basicInfo.vehicleBrandLogo,
        vehicleModel: basicInfo.vehicleModel,
        vehicleModelYear: basicInfo.vehicleModelYear,
        vehicleMileage: basicInfo.vehicleMileage,
        serviceAgentName: basicInfo.serviceAgentName,
        vehicleImageUrl: basicInfo.vehicleImageUrl,
      };
    }, [
      basicInfo.licensePlateNo,
      basicInfo.serviceAgentName,
      basicInfo.taskNo,
      basicInfo.vehicleBrandLogo,
      basicInfo.vehicleBrandName,
      basicInfo.vehicleImageUrl,
      basicInfo.vehicleMileage,
      basicInfo.vehicleModel,
      basicInfo.vehicleModelYear,
      basicInfo.vehicleName,
      basicInfo.vin,
    ]);

    return (
      <View
        css={`
          height: ${rpx(kVehicleBannerHeight)}px;
        `}
        style={style}
      >
        <LinearGradient
          style={StyleSheet.absoluteFill}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          colors={[
            theme.task.detail.vehicleBanner.startColor,
            theme.task.detail.vehicleBanner.endColor,
          ]}
          css={`
            border-radius: ${rpx(20)}px;
          `}
        />
        <HStack
          css={`
            flex: 1;
            padding-left: ${rpx(48)}px;
          `}
        >
          <View
            css={`
              justify-content: center;
            `}
          >
            <HStack justifyContent="flex-start">
              <VehicleBrandLogo
                url={basicInfo.vehicleBrandLogo}
                height={rpx(40)}
              />
              <Typography.Label
                bold
                color={theme.task.detail.vehicleBanner.vehicleNameTextColor}
                size={rpx(32)}
                css={`
                  margin-left: ${rpx(24)}px;
                `}
              >
                {basicInfo.vehicleName}
              </Typography.Label>
              <TaskBookmarkStatusView
                payload={bookmarkPayload}
                iconSize={rpx(36)}
                iconColor={theme.task.detail.vehicleBanner.starIconColor}
                outlineIconColor={
                  theme.task.detail.vehicleBanner.outlineStarIconColor
                }
                css={`
                  margin-left: ${rpx(16)}px;
                `}
              />
            </HStack>
            <ScrollView
              horizontal
              contentContainerStyle={{ alignItems: 'center' }}
              showsHorizontalScrollIndicator={false}
              css={`
                flex-grow: 0;
                margin-top: ${rpx(38)}px;
              `}
            >
              <Cell
                theme={theme}
                label="里程 (公里)"
                text={`${basicInfo.vehicleMileage}`}
              />
              <Separator />
              <Cell
                theme={theme}
                label="服务顾问"
                text={basicInfo.serviceAgentName}
              />
              <Separator />
              <Cell
                theme={theme}
                label="进店时间"
                text={formatDateTime(basicInfo.createdAt, 'MM-dd HH:mm')}
              />
              <Separator />
              <Cell
                theme={theme}
                label="关注服务"
                text={
                  <ObserveStatus
                    hasObserved={hasObserved}
                    onPress={onQrcodeSharingPress}
                    color={
                      hasObserved
                        ? theme.task.detail.vehicleBanner.observedStatusColor
                        : theme.task.detail.vehicleBanner.unobservedStatusColor
                    }
                  />
                }
              />
              {basicInfo.status === InspectionTaskStatus.Finished && (
                <>
                  <Separator />
                  <Cell
                    theme={theme}
                    label="完成时间"
                    text={formatDateTime(
                      (basicInfo.finishedAt ?? basicInfo.updatedAt)!,
                      'MM-dd HH:mm',
                    )}
                  />
                </>
              )}
            </ScrollView>
          </View>
          <Img
            uri={basicInfo.vehicleImageUrl}
            source={
              basicInfo.vehicleImageUrl
                ? undefined
                : require('@euler/assets/img/default-car-img.png')
            }
            css={`
              width: ${rpx(416)}px;
              height: ${rpx(210)}px;
              margin-left: ${rpx(27)}px;
              flex-shrink: 0;
              margin-right: ${rpx(70)}px;
            `}
          />
        </HStack>
      </View>
    );
  },
);
